import {
  EntityAdapter,
  EntityState,
  Update,
  createEntityAdapter,
} from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import {
  CasualTournamentEntity,
  EsRangeValues,
  EventsCountByTag,
  Game,
  TournamentType,
  TournamentsFilters,
  TournamentsQueryParams,
  TournamentsResponse,
} from '@fantaz/shared/data-access/models';
import * as TournamentsActions from './tournaments.actions';

export const TOURNAMENTS_FEATURE_KEY = 'tournaments';

export interface TournamentsState extends EntityState<CasualTournamentEntity> {
  cashTournaments?: TournamentsResponse;
  prizeTournaments?: TournamentsResponse;
  params: TournamentsQueryParams;
  filters?: TournamentsFilters;
  eventsCountByTag?: EventsCountByTag[];
  gamesByEventsList: Game[];
  esRangeValues?: EsRangeValues;
  filterByGames?: string[];
  filterByTags?: string[];
  searchTerm?: string;
  loaded: boolean;
  loading: boolean;
  selectedId?: string | number;
  filtering: boolean;
  error?: string | null;
  errorLoadingMore?: string | null;
  pagesLoaded: { cash: number[]; prize: number[] };
}

export interface CasualTournamentPartialState {
  readonly [TOURNAMENTS_FEATURE_KEY]: TournamentsState;
}

export const casualTournamentAdapter: EntityAdapter<CasualTournamentEntity> =
  createEntityAdapter<CasualTournamentEntity>({
    selectId: (prizeTournaments: CasualTournamentEntity) =>
      prizeTournaments.uuid,
  });

export const initialCasualTournamentState: TournamentsState =
  casualTournamentAdapter.getInitialState({
    filterByTags: [],
    filterByGames: [],
    eventsCountByTag: [],
    gamesByEventsList: [],
    searchTerm: '',
    loading: false,
    params: {
      activeTournaments: true,
      comingSoonOnly: true,
      retiredEvents: false,
      tournamentType: TournamentType.PRIZE,
      tournamentTags: [],
      getEsRanges: true,
      pageIndex: 0,
      pageSize: 30,
    },
    filtering: false,
    loaded: false,
    pagesLoaded: { cash: [], prize: [] },
  });

const reducer = createReducer(
  initialCasualTournamentState,

  on(
    TournamentsActions.loadTournaments,
    (state: TournamentsState, { params }) => ({
      ...state,
      filterByTags: params.tournamentTags || state.filterByTags || [],
      filterByGames: params.gameIds || state.filterByGames || [],
      searchTerm: params.search || '',
      params: {
        ...state.params,
        ...params,
      },
      loading: true,
      loaded: false,
      error: null,
    }),
  ),
  on(
    TournamentsActions.loadTournamentsSuccess,
    (state, { cashTournaments, prizeTournaments }) => {
      const allGames = [
        ...((cashTournaments && cashTournaments.gamesByEventsList) || []),
        ...((prizeTournaments && prizeTournaments.gamesByEventsList) || []),
      ];

      const gamesByEventsList = [
        ...new Map(allGames.map((v) => [v.key, v])).values(),
      ];

      updatePageLoaded(
        state.pagesLoaded,
        cashTournaments?.pageIndex,
        prizeTournaments?.pageIndex,
      );

      return {
        ...state,
        loaded: true,
        loading: false,
        filters: cashTournaments?.filters || prizeTournaments?.filters || {},
        eventsCountByTag:
          cashTournaments?.eventsCountByTag ||
          prizeTournaments?.eventsCountByTag ||
          [],
        gamesByEventsList,
        esRangeValues:
          cashTournaments?.esRangeValues ||
          prizeTournaments?.esRangeValues ||
          {},
        cashTournaments,
        prizeTournaments,
      };
    },
  ),

  on(TournamentsActions.loadTournamentsFailure, (state, { error }) => ({
    ...state,
    loaded: false,
    error,
  })),
  on(
    TournamentsActions.loadMoreTournaments,
    (state: TournamentsState, { params }) => ({
      ...state,
      loading: true,
      error: null,
      errorLoadingMore: null,
      filterByTags: params.tournamentTags || state.filterByTags || [],
      filterByGames: params.gameIds || state.filterByGames || [],
      searchTerm: params.search || '',
      params: {
        ...state.params,
        ...params,
      },
    }),
  ),
  on(
    TournamentsActions.loadMoreTournamentsSuccess,
    (state, { cashTournaments, prizeTournaments }) => {
      const updatedCashTournamentsItems = [
        ...(state.cashTournaments?.items || []),
        ...(cashTournaments?.items || []),
      ];
      const updatedPrizeTournamentsItems = [
        ...(state.prizeTournaments?.items || []),
        ...(prizeTournaments?.items || []),
      ];
      updatePageLoaded(
        state.pagesLoaded,
        cashTournaments?.pageIndex,
        prizeTournaments?.pageIndex,
      );
      return {
        ...state,
        loading: false,
        errorLoadingMore: null,
        esRangeValues:
          {
            ...cashTournaments?.esRangeValues,
            ...prizeTournaments?.esRangeValues,
          } || {},
        cashTournaments: {
          ...(cashTournaments
            ? {
                ...cashTournaments,
                items: updatedCashTournamentsItems,
              }
            : (state.cashTournaments as TournamentsResponse)),
        },
        prizeTournaments: {
          ...(prizeTournaments
            ? {
                ...prizeTournaments,
                items: updatedPrizeTournamentsItems,
              }
            : (state.prizeTournaments as TournamentsResponse)),
        },
      };
    },
  ),
  on(TournamentsActions.loadMoreTournamentsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    errorLoadingMore: error,
  })),
  on(
    TournamentsActions.loadPageTournaments,
    (state: TournamentsState, { params }) => ({
      ...state,
      loading: true,
      error: null,
      errorLoadingMore: null,
      filterByTags: params.tournamentTags || state.filterByTags || [],
      filterByGames: params.gameIds || state.filterByGames || [],
      searchTerm: params.search || '',
      params: {
        ...state.params,
        ...params,
      },
    }),
  ),
  on(
    TournamentsActions.loadPageTournamentsSuccess,
    (state, { cashTournaments, prizeTournaments }) => {
      const updatedCashTournamentsItems = [...(cashTournaments?.items || [])];
      const updatedPrizeTournamentsItems = [...(prizeTournaments?.items || [])];
      updatePageLoaded(
        state.pagesLoaded,
        cashTournaments?.pageIndex,
        prizeTournaments?.pageIndex,
        true,
      );
      return {
        ...state,
        loading: false,
        errorLoadingMore: null,
        esRangeValues:
          {
            ...cashTournaments?.esRangeValues,
            ...prizeTournaments?.esRangeValues,
          } || {},
        cashTournaments: {
          ...(cashTournaments
            ? {
                ...cashTournaments,
                items: updatedCashTournamentsItems,
              }
            : (state.cashTournaments as TournamentsResponse)),
        },
        prizeTournaments: {
          ...(prizeTournaments
            ? {
                ...prizeTournaments,
                items: updatedPrizeTournamentsItems,
              }
            : (state.prizeTournaments as TournamentsResponse)),
        },
      };
    },
  ),
  on(TournamentsActions.loadPageTournamentsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    errorLoadingMore: error,
  })),
  on(
    TournamentsActions.filterTournaments,
    (state: TournamentsState, { params }) => ({
      ...state,
      filtering: true,
      loading: true,
      error: null,
      filterByTags: params.tournamentTags || state.filterByTags || [],
      filterByGames: params.gameIds || state.filterByGames || [],
      searchTerm: params.search || '',
      params: {
        ...state.params,
        ...params,
      },
      pagesLoaded: {
        cash: [],
        prize: [],
      },
    }),
  ),
  on(
    TournamentsActions.filterTournamentsSuccess,
    (state, { cashTournaments, prizeTournaments }) => {
      const allGames = [
        ...((cashTournaments && cashTournaments.gamesByEventsList) || []),
        ...((prizeTournaments && prizeTournaments.gamesByEventsList) || []),
      ];

      const gamesByEventsList = [
        ...new Map(allGames.map((v) => [v.key, v])).values(),
      ];
      updatePageLoaded(
        state.pagesLoaded,
        cashTournaments?.pageIndex,
        prizeTournaments?.pageIndex,
        true,
      );
      return {
        ...state,
        loading: false,
        filtering: false,
        ...(gamesByEventsList.length && { gamesByEventsList }),
        cashTournaments,
        prizeTournaments,
      };
    },
  ),
  on(TournamentsActions.filterTournamentsFailure, (state, { error }) => ({
    ...state,
    filtering: false,
    error,
    errorLoadingMore: error,
  })),
  on(TournamentsActions.clearFilterTournamentsByGames, (state) => ({
    ...state,
    filterByGames: [],
  })),
  on(TournamentsActions.clearFilterTournamentsByTags, (state) => ({
    ...state,
    filterByTags: [],
  })),
  on(TournamentsActions.clearSearchTournaments, (state) => ({
    ...state,
    searchTerm: '',
  })),
  on(TournamentsActions.clearAllFilters, (state) => ({
    ...state,
    searchTerm: '',
    filterByTags: [],
    filterByGames: [],
    params: {
      ...state.params,
      pageIndex: 0,
    },
  })),
  on(
    TournamentsActions.loadSubscriptionTournamentSuccess,
    (state, { eventPrototypeUuid, eventSchedule, status }) => {
      const eventScheduleToUpdate = {
        ...eventSchedule,
        subscriptionStatus: status,
      };
      const eventSchedules = [];
      eventSchedules.push(eventScheduleToUpdate);
      const update: Update<CasualTournamentEntity> = {
        id: eventPrototypeUuid,
        changes: { eventSchedules },
      };
      return casualTournamentAdapter.updateOne(update, state);
    },
  ),
);

export function tournamentsReducer(
  state: TournamentsState | undefined,
  action: Action,
) {
  return reducer(state, action);
}
function updatePageLoaded(
  pagesLoaded: { cash: number[]; prize: number[] },
  cashPageIndex: number,
  prizePageIndex: number,
  replace?: boolean,
) {
  if (cashPageIndex >= 0 && pagesLoaded.cash.indexOf(cashPageIndex) === -1) {
    if (!replace) {
      pagesLoaded = {
        ...pagesLoaded,
        cash: [...pagesLoaded.cash, cashPageIndex],
      };
    } else {
      pagesLoaded = {
        ...pagesLoaded,
        cash: [...pagesLoaded.cash, cashPageIndex],
      };
    }
  }
  if (prizePageIndex >= 0 && pagesLoaded.prize.indexOf(prizePageIndex) === -1) {
    if (!replace) {
      pagesLoaded = {
        ...pagesLoaded,
        prize: [...pagesLoaded.prize, prizePageIndex],
      };
    } else {
      pagesLoaded = {
        ...pagesLoaded,
        prize: [...pagesLoaded.prize, prizePageIndex],
      };
    }
  }
}
